import { useEffect, useState } from "react";
import apiClient from "@/helpers/api.helper";
import { CXC_OPTIONS } from "@/constants/urls";

// CXC sittings, deadlines and fees come from the server
// (GET /CxcSettings/options) - the one copy staff edit in the admin app
// (Academic > CXC > Subject Configuration) and the same one the server checks
// every submission against. Nothing here is hard-coded any more: a new year,
// a moved deadline or a new price needs no website release.

export interface CxcFees {
  centreFee: number;
  registrationFee: number;
  processingFee: number;
  subjectFee: number;
  practicalFee: number;
  lateFee: number;
  /** Optional cap on subjects; null = only the subjects on offer limit it. */
  maxSubjects: number | null;
}

export interface CxcSittingOption {
  id: string;
  /** "January" | "MayJune" - what the registration stores. */
  sittingMonth: string;
  sittingYear: number;
  /** "January 2027", "May/June 2027". */
  label: string;
  /** yyyy-mm-dd, the last day to register (inclusive, Cayman time). */
  registrationDeadline: string;
  /** "Friday, 09 October 2026". */
  deadlineDisplay: string;
  isOpen: boolean;
  daysRemaining: number;
}

export interface CxcOptions {
  formCode: string;
  centreName: string;
  centreNumber: string;
  currency: string;
  isOpen: boolean;
  sittings: CxcSittingOption[];
  fees: CxcFees;
}

/** One schedule row for the header and banner - the shape they always rendered. */
export interface CxcSittingSchedule {
  /** "January-2027": month AND year - two open January sittings are two rows. */
  key: string;
  name: string;
  sittingLabel: string;
  examYear: number;
  deadlineDisplay: string;
  isOpen: boolean;
  daysRemaining: number;
}

export interface CxcRegistrationStatus {
  isOpen: boolean;
  activeSchedules: CxcSittingSchedule[];
  allSchedules: CxcSittingSchedule[];
  nextDeadlineSchedule: CxcSittingSchedule | null;
  nextDeadlineDaysRemaining: number;
  activeBadgeText: string;
}

/** The status the header, banner and form show, straight from the server's view of what is open. */
export function statusFromOptions(options: CxcOptions): CxcRegistrationStatus {
  const allSchedules: CxcSittingSchedule[] = options.sittings.map((s) => ({
    key: `${s.sittingMonth}-${s.sittingYear}`,
    name: `${s.label.replace(/ \d{4}$/, "")} Registration`,
    sittingLabel: `${s.label} Sitting`,
    examYear: s.sittingYear,
    deadlineDisplay: s.deadlineDisplay,
    isOpen: s.isOpen,
    daysRemaining: s.daysRemaining,
  }));

  // The server sends sittings soonest-deadline first.
  const activeSchedules = allSchedules.filter((s) => s.isOpen);
  const next = activeSchedules[0] ?? null;

  return {
    isOpen: activeSchedules.length > 0,
    activeSchedules,
    allSchedules,
    nextDeadlineSchedule: next,
    nextDeadlineDaysRemaining: next?.daysRemaining ?? 0,
    activeBadgeText:
      activeSchedules.length === 0
        ? "CXC Registration"
        : activeSchedules.map((s) => s.sittingLabel.replace(/ Sitting$/, "")).join(" & "),
  };
}

/** "Oct 9" from "Friday, 09 October 2026", for the compact header button. */
export function shortDeadline(deadlineDisplay: string): string {
  const m = /(\d{1,2}) ([A-Za-z]+) \d{4}/.exec(deadlineDisplay);
  return m ? `${m[2].slice(0, 3)} ${Number(m[1])}` : deadlineDisplay;
}

let pending: Promise<CxcOptions> | null = null;

/** One request per page load, shared by every component that asks. A failure is retried on the next ask. */
export function loadCxcOptions(): Promise<CxcOptions> {
  if (!pending) {
    pending = apiClient.get<CxcOptions>(CXC_OPTIONS).catch((err) => {
      pending = null;
      throw err;
    });
  }
  return pending;
}

/** The options for the public (GCP) form; null until they arrive. */
export function useCxcOptions(): { options: CxcOptions | null; error: string | null } {
  const [options, setOptions] = useState<CxcOptions | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCxcOptions()
      .then((o) => {
        if (!cancelled) setOptions(o);
      })
      .catch(() => {
        if (!cancelled) setError("The CXC registration details could not be loaded. Please refresh the page.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, error };
}

/**
 * The header/banner status; null until known. If the server cannot be
 * reached the highlight simply stays hidden - it never guesses from a date
 * compiled into the site.
 */
export function useCxcRegistrationStatus(): CxcRegistrationStatus | null {
  const { options } = useCxcOptions();
  return options ? statusFromOptions(options) : null;
}
